import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { ArrowRight, Wrench, FileText, Truck, KeyRound } from "lucide-react";
import { LogoMark } from "../components/Logo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1777190772770-895ec6416335?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxjYXIlMjBtZXRhbGxpYyUyMGRldGFpbHxlbnwwfHx8fDE3NzgxNDc3NzZ8MA&ixlib=rb-4.1.0&q=85";
const MECH_IMG =
  "https://images.unsplash.com/photo-1775590766345-c117265f0c1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxhdXRvbW90aXZlJTIwaW5zcGVjdGlvbiUyMG1lY2hhbmljfGVufDB8fHx8MTc3ODE0Nzc3Nnww&ixlib=rb-4.1.0&q=85";

const SERVICE_ICONS = [Wrench, FileText, Truck, KeyRound];

export default function Home() {
  const { t } = useI18n();

  return (
    <div data-testid="page-home" className="bg-ct3f-bg text-white">
      {/* HERO */}
      <section className="border-b border-white/10 bg-flame-radial" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-6 md:px-12 lg:px-20 py-16 lg:py-24 flex flex-col justify-between min-h-[620px] lg:min-h-[700px] relative">
            <div className="absolute inset-0 grid-lines pointer-events-none opacity-50" />
            <div className="relative flex items-center justify-between">
              <span className="label-tech text-ct3f-amber">{t.home.eyebrow}</span>
              <span className="label-tech text-white/35 hidden md:inline">№ 003 · TRI-FRONTIÈRES</span>
            </div>

            <div className="relative mt-12">
              <div className="flex items-center gap-4 mb-8">
                <LogoMark size={56} />
                <span className="label-tech text-white/50">CT3F · LU · FR · BE</span>
              </div>
              <h1 className="font-display font-black uppercase tracking-tighter text-[11.5vw] sm:text-7xl lg:text-[7.5rem] leading-[0.9]">
                <span className="block">{t.home.title1}</span>
                <span className="block">{t.home.title2}</span>
                <span className="block">
                  <span className="text-flame">{t.home.title3.replace(".", "")}</span>
                  <span className="text-white">.</span>
                </span>
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
                {t.home.sub}
              </p>
            </div>

            <div className="relative mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-flame text-white label-tech px-7 py-4 hover:brightness-110 ring-flame transition-all"
              >
                {t.home.ctaPrimary}
                <ArrowRight size={16} className="hover-arrow" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/30 text-white label-tech px-7 py-4 hover:bg-white hover:text-ct3f-bg transition-colors"
              >
                {t.home.ctaSecondary}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative bg-black min-h-[420px] lg:min-h-[700px] border-t lg:border-t-0 lg:border-l border-white/10">
            <img
              src={HERO_IMG}
              alt="Vehicle close-up"
              className="absolute inset-0 w-full h-full object-cover opacity-85"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/30 to-transparent" />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-60"
              style={{
                background:
                  "radial-gradient(circle at 70% 30%, rgba(248,154,43,0.4), transparent 55%), radial-gradient(circle at 20% 80%, rgba(230,58,26,0.4), transparent 55%)",
              }}
            />
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
              <div className="flex gap-2">
                {["LU", "FR", "BE"].map((c) => (
                  <span
                    key={c}
                    className="label-tech bg-white/10 backdrop-blur text-white border border-white/20 px-2 py-1"
                    data-testid={`hero-tag-${c.toLowerCase()}`}
                  >
                    [ {c} ]
                  </span>
                ))}
              </div>
              <span className="label-tech text-white/80">CT3F · 2025</span>
            </div>
            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-3 gap-3">
              {t.home.stats.map((s, i) => (
                <div key={i} className="bg-black/65 backdrop-blur px-4 py-3 border border-white/15">
                  <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-flame">
                    {s.k}
                  </div>
                  <div className="label-tech text-white/65">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="border-b border-white/10 bg-ct3f-bg" data-testid="services-preview">
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-4">
              <span className="label-tech text-ct3f-amber">{t.home.sectionLabel1}</span>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl whitespace-pre-line leading-[0.95] text-white">
                {t.home.sectionTitle1}
              </h2>
              <p className="mt-6 max-w-2xl text-white/65">{t.home.sectionDesc1}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
            {t.services.list.map((s, i) => {
              const Icon = SERVICE_ICONS[i] || Wrench;
              return (
                <div
                  key={s.k}
                  data-testid={`service-card-${i}`}
                  className="group p-8 lg:p-10 border-r border-b border-white/10 hover:bg-ct3f-surface transition-colors relative overflow-hidden"
                >
                  <div className="flex items-start justify-between">
                    <span className="label-tech text-ct3f-amber">{s.k}</span>
                    <Icon size={28} strokeWidth={1.5} className="text-white/40 group-hover:text-ct3f-orange transition-colors" />
                  </div>
                  <h3 className="font-display font-bold text-2xl lg:text-3xl mt-8 leading-tight text-white">
                    {s.t}
                  </h3>
                  <p className="mt-4 text-white/60 leading-relaxed">{s.d}</p>
                  <Link
                    to="/services"
                    className="mt-6 inline-flex items-center gap-2 label-tech text-white group-hover:text-ct3f-amber transition-colors"
                  >
                    {t.common.readMore} <span className="hover-arrow">→</span>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="border-b border-white/10 bg-[#0E0E10] text-white relative overflow-hidden" data-testid="regions-section">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background:
              "radial-gradient(circle at 80% 50%, rgba(242,90,41,0.18), transparent 50%), radial-gradient(circle at 20% 80%, rgba(248,154,43,0.12), transparent 55%)",
          }}
        />
        <div className="relative px-6 md:px-12 lg:px-20 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-4">
              <span className="label-tech text-ct3f-amber">{t.home.sectionLabel2}</span>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl whitespace-pre-line leading-[0.95]">
                {t.home.sectionTitle2}
              </h2>
              <p className="mt-6 max-w-2xl text-white/70">{t.home.sectionDesc2}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {Object.entries(t.home.regions).map(([code, r], idx) => (
              <div
                key={code}
                data-testid={`region-${code}`}
                className="p-8 lg:p-10 border-r border-b border-white/10 bg-white/[0.02]"
              >
                <div className="flex items-center gap-3">
                  <span className="label-tech border border-ct3f-orange/60 text-ct3f-amber px-2 py-1">[ {code.toUpperCase()} ]</span>
                  <span className="label-tech text-white/40">REGION 0{idx + 1}</span>
                </div>
                <h3 className="font-display font-bold text-3xl lg:text-4xl mt-8 tracking-tight text-white">{r.name}</h3>
                <p className="mt-4 text-white/65 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="border-b border-white/10 bg-ct3f-bg" data-testid="why-section">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 relative min-h-[400px] lg:min-h-[640px] border-b lg:border-b-0 lg:border-r border-white/10">
            <img src={MECH_IMG} alt="Mechanic" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-black/40 to-transparent" />
            <div
              className="absolute inset-0 mix-blend-overlay opacity-50"
              style={{
                background:
                  "radial-gradient(circle at 80% 70%, rgba(242,90,41,0.4), transparent 50%)",
              }}
            />
            <div className="absolute top-6 left-6 label-tech bg-white text-ct3f-bg px-3 py-1">
              ATELIER · CT3F
            </div>
            <div className="absolute bottom-6 left-6">
              <LogoMark size={56} />
            </div>
          </div>
          <div className="lg:col-span-7 px-6 md:px-12 lg:px-16 py-20 lg:py-28">
            <span className="label-tech text-ct3f-amber">{t.home.sectionLabel3}</span>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl lg:text-6xl whitespace-pre-line leading-[0.95] mt-4 text-white">
              {t.home.sectionTitle3}
            </h2>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              {t.home.reasons.map((r, i) => (
                <div key={i} className="border-t border-white/15 pt-5" data-testid={`reason-${i}`}>
                  <div className="label-tech text-ct3f-amber">0{i + 1}</div>
                  <h4 className="font-display font-bold text-xl mt-3 text-white">{r.t}</h4>
                  <p className="text-white/65 mt-2 leading-relaxed">{r.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-flame text-white relative overflow-hidden" data-testid="home-cta-section">
        <div
          className="absolute inset-0 opacity-30 mix-blend-overlay"
          style={{
            background:
              "radial-gradient(circle at 90% 10%, rgba(255,255,255,0.4), transparent 50%)",
          }}
        />
        <div className="relative px-6 md:px-12 lg:px-20 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="label-tech text-white/85">CT3F · CONTACT</span>
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl lg:text-7xl mt-4 leading-[0.95]">
                {t.home.ctaTitle}.
              </h2>
              <p className="mt-6 max-w-2xl text-white/95 text-lg">{t.home.ctaDesc}</p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/contact"
                data-testid="home-cta-button"
                className="group inline-flex items-center gap-3 bg-ct3f-bg text-white label-tech px-8 py-5 hover:bg-black transition-colors"
              >
                {t.home.ctaButton}
                <ArrowRight size={18} className="hover-arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
